import { useMemo, useState } from "react";
import { useT } from "../i18n";
import { FehlerBand, Laedt } from "../bausteine";
import { Deckblatt } from "../bausteine/Deckblatt";
import { Blattvonunten } from "../bausteine/Blattvonunten";
import {
  Tagekopf, Trefferzeile, tagesspalten, useTagesbreite,
} from "../bausteine/Trefferzeile";
import { Trefferauskunft } from "../bausteine/Trefferauskunft";
import { useFeiertagAm } from "../bausteine/Feiertage";
import { Zeitfrage, zeitVorgabe, type Zeitwunsch } from "../bausteine/Zeitfrage";
import { mitAusstattung } from "../bausteine/Ausstattung";
import { useRuf } from "../schale/Ruf";
import { nachschlagenSchliessen, useNachschlagenOffen } from "../schale/nachschlagen";
import { useMaschinentypen } from "../daten/haken";
import { ladestandVon } from "../daten/ladestand";
import { useNachschlagen, type Typenlage } from "../daten/nachschlagen";
import { usePreisbuendel } from "../daten/preisbuendel";
import { kopfMiettage, zeilenfrage } from "../daten/kopfmiettage";
import { alsAnhaenger } from "../daten/abholung";
import { useMerkliste, warSchonGemerkt } from "../daten/merkliste";
import { useTelefon } from "../schale/geraet";
import { NachschlagenTelefon } from "./NachschlagenTelefon";
import "../stile/vermieten.css";
/* Die Telefonregeln — direkt nach der Schreibtischdatei, damit die Kaskade
   klar ist: erst die des Vermietens (die Trefferzeile am Telefon steht dort),
   dann die eigenen. */
import "../stile/telefon/vermieten.css";
import "../stile/telefon/nachschlagen.css";
import "../stile/pillen.css";
/* Die Liste trägt `.tab-rahmen`/`.tab-fenster`, und nur dort steht, dass
   das Fenster rollt. Die Regeln kamen bis zum 17.09.2026 nur mit
   `bausteine/Tabelle.tsx` herein — wer das Blatt auf der Startseite
   öffnete, bevor irgendeine Liste geladen war, hatte ein Fenster ohne
   `overflow` und eine Liste, die unten abgeschnitten war statt zu rollen. */
import "../stile/tabelle.css";

/* ══════════════════════════════════════════════════════════════════
   Nachschlagen — ein Blatt statt zweier.

   Luca, 17.09.2026, nachdem die Vermietfläche stand:

   > *„Ich verstehe jetzt mittlerweile nicht mehr, wozu ich dann noch
   > Preisauskunft und Verfügbarkeits-Check brauche. Warum kann ich das
   > nicht genau so gut dann auch in der Direktvermietung machen und das
   > jetzt merken? Weiß ich nicht."*

   Er hat recht, und der Grund ist der Tagesstreifen: Seit die
   Trefferzeile **Lage, Woche und Preis zugleich** zeigt, beantwortet die
   Fläche beide Fragen besser als die beiden Blätter — und sie kann
   merken. Aus „Preisauskunft" und „Verfügbarkeitscheck" wird deshalb
   **eines**, und es heißt, wie es am Telefon schon hieß
   (`/handy/nachschlagen`, seit dem 17.09.2026 dieses Blatt): zwei Namen
   für dieselbe Sache waren nie eine gute Idee.

   ── Wofür es dann noch da ist ─────────────────────────────────────

   Für **einen** Fall, und nur einen: den Blick **aus einer fremden Maske
   heraus, ohne sie zu verlassen.** Wer in der Kundenakte sitzt und am
   Telefon gefragt wird, was der Bagger nächste Woche kostet, darf die
   Akte nicht verlieren — genau dafür ist das Deckblatt gebaut
   (`bausteine/Deckblatt.tsx`). Und am Telefon, wo die Fläche nicht
   hinpasst — dort füllt es den Schirm (`NachschlagenTelefon.tsx`).

   Von der Startseite aus braucht es niemand mehr: Dort ist der Weg auf
   die Fläche genauso kurz, und dort kann man mehr. Die beiden Kacheln
   sind deshalb weg (`masken/Start.tsx`).

   ── Was darin steht, und was nicht ────────────────────────────────

   **Die Frage oben: Gerät und Zeitraum.** Kunde und Einsatzort stehen
   hier nicht — das ist die Sache der Fläche. Wer weiß, für wen es ist,
   ist nicht mehr beim Nachschlagen, sondern beim Vermieten.

   **Kein Umschalten mehr.** Es gab zwei Sichten mit einem Weg
   dazwischen („Preis dazu", „Zurück zur Verfügbarkeit"), und beide
   zeigten Teile derselben Antwort. Jetzt gibt es eine Sicht, und die
   kann beides. Mit dem Umschalten fallen `sicht`,
   `PREISAUSKUNFT_NIMMT_VORGABE` und `zurueckZurLage` weg.

   **Gemerkt wird in dieselbe Sammlung** wie aus der Fläche — dieselbe
   Merkliste, derselbe Abschluss (`daten/merkliste.ts`). Das war schon
   so und darf beim Umbau nicht verlorengehen.

   Klassen: `ns-*` und die geteilten `tz-`/`zf-`/`ta-`, gegen alle Stile
   geprüft.
   ══════════════════════════════════════════════════════════════════ */

export function Nachschlagen() {
  const t = useT();
  const offen = useNachschlagenOffen();
  /* **Am Telefon dasselbe Blatt, für die Hand gebaut** (17.09.2026). Darin
     steht statt der Zeile mit Streifen die Telefonfassung mit Rechenweg
     (`NachschlagenTelefon.tsx`). `/nachschlagen` und `/preis` (bis zum
     19.09.2026 unter `/handy/…`) öffnen dieses Blatt und leiten zurück
     (`router.tsx`). */
  const telefon = useTelefon();

  /* ── Am Telefon ein Blatt von unten, kein Deckblatt (18.09.2026) ──

     Luca, nach dem Durchklicken: *„Dieser Schließen-Knopf — macht das auch
     als Bottom Sheet, sodass ich es runterziehen kann. Oben kannst du
     meinetwegen ein X in die Ecke machen."*

     Bis hierher füllte das Deckblatt den Schirm, und oben rechts stand der
     Textknopf „Schließen". Jetzt kommt das Blatt von unten: Ziehgriff,
     Kreuz in der Ecke, nach unten wegziehbar, `Esc` und die Zurückgeste
     schließen es weiter (`bausteine/Blattvonunten.tsx`).

     **`form="unten"`, obwohl ein Suchfeld darin steht.** Die Regel für
     Blätter macht daraus sonst ein Vollbild (dort, „Die Regel für Blätter
     am Telefon"), weil die Tastatur einem Blatt von 80 % den Platz nimmt.
     Hier ist der Streifen Maske über dem Blatt aber kein Verlust, sondern
     die Auskunft darüber, dass sich etwas herunterziehen lässt — und genau
     darum ging es Luca. Das Suchfeld ist außerdem nicht der Einstieg,
     sondern die Abkürzung für den, der die Inventarnummer kennt; der
     Regelweg sind Gerätegruppe und Zeitraum, und dafür geht keine Tastatur
     auf.

     ── `rast="gross"`: hier wird gearbeitet (18.09.2026) ──────────────

     Luca: *„Das Bottom Sheet musst du deutlich höher machen, damit man mehr
     Platz hat."*

     **Gemessen auf 390 × 844**, mit dem kleinen Rastpunkt: Das Blatt war
     359 px hoch, davon 48 px Griff und 58 px Kopf. Vom Leib blieben 252 px,
     und darin stehen erst einmal Suchfeld, Gerätegruppen und Zeitraum —
     **keine einzige ganze Trefferzeile** war zu sehen. Mit `gross` sind es
     593 px und **vier Zeilen**.

     Das ist kein Widerspruch zu dem Satz von heute früh, dass die Blätter
     kleiner werden sollen: Der galt der **Auswahl** — Kundenwahl, Notiz,
     Suche —, wo wenig drinsteht und die Maske darunter die Auskunft ist.
     Hier geht man eine Liste durch. Die Regel dazu steht in
     40-mobiles-grundgeruest.md („Ein Blatt, aus dem man auswählt, bleibt
     klein; ein Blatt, in dem man arbeitet, wird groß"). */
  if (telefon) {
    return (
      <Blattvonunten
        offen={offen}
        form="unten"
        rast="gross"
        schliessen={nachschlagenSchliessen}
        titel={t("nachschlagen.titel")}
        unter={t("nachschlagen.unter")}
      >
        <NachschlagenTelefon />
      </Blattvonunten>
    );
  }

  return (
    <Deckblatt
      offen={offen}
      kompakt
      /* Breiter als das Kompaktmaß: Die Zeile trägt Name, Streifen, Lage,
         Miettage und Betrag nebeneinander, und der Pillenkranz geht über
         sieben Gerätegruppen. Bei 720 px bricht beides um (Luca,
         16.09.2026). Etwas breiter als die alten 880, weil jetzt auch der
         Streifen hineinmuss. */
      weite={960}
      schliessen={nachschlagenSchliessen}
      titel={t("nachschlagen.titel")}
      untertitel={t("nachschlagen.unter")}
    >
      {/* Erst wenn es offen ist, wird gefragt. Ein Blatt, das zu ist und
          trotzdem jede Sekunde den Bestand abruft, kostet jede Maske
          etwas, in der es nicht gebraucht wird. */}
      {offen ? <Blatt /> : null}
    </Deckblatt>
  );
}

function Blatt() {
  const t = useT();
  const ruf = useRuf();
  const { merkenWennFrei } = useMerkliste();

  const [wunsch, setzeWunsch] = useState<Zeitwunsch>(zeitVorgabe);
  const [wort, setzeWort] = useState("");
  const [gruppe, setzeGruppe] = useState<string | null>(null);
  const [markiert, setzeMarkiert] = useState<string | null>(null);

  /* ── Der Kalender ────────────────────────────────────────────────
     Wie viele Miettage der gefragte Zeitraum hat — und bei „ab Montag,
     fünf Tage" überhaupt erst, bis wann er reicht. Beides rechnet die
     andere Seite (`GET /preise`, `fach.miettageErklaert`); dieselbe
     Route, dieselbe Rechnung wie in der Fläche. */
  const kalenderfrage = useMemo(() => ({
    typen: [] as string[],
    von: wunsch.von,
    ...(wunsch.bis ? { bis: wunsch.bis } : { miettage: wunsch.tage }),
  }), [wunsch.von, wunsch.bis, wunsch.tage]);
  const { data: kalender } = usePreisbuendel(kalenderfrage);
  const passt = kalender?.von === wunsch.von;
  const bis = wunsch.bis ?? (passt ? kalender.kalender.bis : null);

  const antwort = useNachschlagen(
    { wort, gruppe, von: wunsch.von, bis: bis ?? wunsch.von },
    Boolean(bis),
  );
  const zeilen = useMemo(() => antwort.data?.zeilen ?? [], [antwort.data]);
  const gefragt = wort.trim().length > 0 || gruppe !== null;

  /* **Ohne Kunden.** Hier steht der Listenpreis — wer die Kondition eines
     Kunden sehen will, ist beim Vermieten und nicht beim Nachschlagen.
     **Und es steht jetzt auch dabei** (18.09.2026, siehe der Fuß unten): Bis
     dahin war das nur ein Kommentar, und eine Zahl ohne Adressat sieht aus
     wie eine mit. */
  const typen = useMemo(() => zeilen.map((z) => z.typId), [zeilen]);
  /* Eine Dauer bleibt eine Dauer, und der Kopf nennt die Miettage, die
     gerechnet werden — wie in der Fläche (V-15N, `daten/kopfmiettage.ts`). */
  const frageZeilen = zeilenfrage(wunsch, bis);
  const preisAntwort = usePreisbuendel(
    { typen, von: wunsch.von, ...(frageZeilen ?? { bis }) },
    typen.length > 0 && Boolean(bis),
  );
  const preise = preisAntwort.data;
  const preisJe = useMemo(
    () => new Map((preise?.zeilen ?? []).map((p) => [p.typId, p])),
    [preise],
  );
  const kopf = kopfMiettage(
    passt ? kalender.kalender : null,
    preise && !preisAntwort.isPlaceholderData && preise.von === wunsch.von ? preise.zeilen : null,
  );

  const { data: typenStamm } = useMaschinentypen();
  const stammJe = useMemo(
    () => new Map((typenStamm?.zeilen ?? []).map((x) => [x.id, x])),
    [typenStamm],
  );

  /* Der Tagesstreifen — dieselbe Messung wie in der Fläche, nur in einem
     schmaleren Fenster. Passt er nicht, sagt die Zeile es in Worten. */
  const streifenTage = zeilen[0]?.tage.length ?? 0;
  const { ref: listeRef, breite: tagBreite, stil: rasterStil } = useTagesbreite(streifenTage);
  const wochentage = t.liste("kalender.wochentageKurz");
  const feiertagAm = useFeiertagAm();
  const spalten = useMemo(
    () => (tagBreite > 0 && zeilen[0]
      ? tagesspalten(zeilen[0].tageAb || wunsch.von, streifenTage, wochentage, feiertagAm)
      : []),
    [tagBreite, zeilen, streifenTage, wochentage, wunsch.von, feiertagAm],
  );

  /* **Gemerkt wird in dieselbe Sammlung wie aus der Fläche**, und mit
     demselben Inhalt: der Zeitraum aus der Preisantwort, die Miettage aus
     derselben, die Zuschläge, die ohne Nachfrage mitlaufen, und was das
     Gerät an Bedienung verlangt (Migration 0083). Was hier fehlt — die
     Ausstattung von Hand, der Fahrer, das Zubehör —, kommt im Blatt am
     Posten dazu; dort steht es für beide Wege gleich. */
  const merkenZeile = async (z: Typenlage) => {
    const p = preisJe.get(z.typId);
    if (!bis) return;
    const stamm = stammJe.get(z.typId);
    const name = mitAusstattung(z.typ, stamm, p?.varianteId ?? "");
    const vorgewaehlt = (p?.zuschlaege ?? []).filter((x) => x.vorgewaehlt);
    // Nur, was frei ist — sonst das Fenster und kein Ruf (19.09.2026).
    const gemerkt = await merkenWennFrei({
      typId: z.typId,
      typ: z.typ,
      bezeichnung: name,
      gruppe: z.uebergruppe || z.gruppe,
      ...(p?.varianteId
        ? {
          varianteId: p.varianteId,
          variante: stamm?.varianten.find((v) => v.id === p.varianteId)?.label ?? "",
        }
        : {}),
      von: wunsch.von,
      bis: p?.bis || bis,
      ...(p && !p.ohnePreis
        ? {
          miettage: p.miettage,
          netto: p.netto,
          einheiten: p.menge,
          einheitKey: p.einheitKey,
          /* **Auf diese Pauschale schlaegt bewusst nichts auf** (0144) — wie
             aus der Vermietflaeche. Ohne diese Zeile saehe ein zuschlagsfreier
             Posten in der Sammlung aus wie einer, fuer den noch niemand
             gerechnet hat. */
          zuschlagsfrei: p.zuschlagsfrei,
          zuschlaegeNetto: vorgewaehlt.reduce((s, x) => s + x.netto, 0),
          zuschlaege: vorgewaehlt.map((x) => ({
            code: x.code, bezeichnung: x.bezeichnung, netto: x.netto,
            menge: x.menge,
            /* **Kein `einzelpreis`** (Durchgang 03.10.2026, V-13/V-15/V-24):
               Das Feld heißt „der zugesagte Satz", und wo es steht, geht der
               Zuschlag als Zeile von Hand hinaus und schlägt die Regel
               (`MerklisteAbschluss`). Bis hierher stand der gerechnete Satz
               darin — jeder vorgewählte Zuschlag wurde so festgeschrieben:
               die Prozentzuschläge auf dem Betrag VOR einem
               Ausstattungswechsel (Kran 43,13 € zu wenig), die Anlieferung
               mit ihrem Satz 0,00 statt dem Mindestbetrag — und eine
               0,00-Anlieferung heißt im Auftrag „Kunde holt ab". */
            ...(x.jeAuftrag ? { jeAuftrag: true } : {}),
            /* Zwei Aussagen, die keine Betraege sind: ein erfundener Satz
               (0032/0036) und der Zuschlag, der bei Selbstabholung entfaellt
               (0127). Die Karte braucht beide. */
            ...(x.platzhalter ? { platzhalter: true } : {}),
            ...(x.faelltBeiAbholung ? { faelltBeiAbholung: true } : {}),
            // Preis immer individuell (0227, E1): „Preis fehlt", bis jemand ihn nennt.
            ...(x.preisIndividuell ? { preisIndividuell: true } : {}),
          })),
        }
        : {}),
      ...(p ? { bedienungspflicht: p.bedienungspflicht } : {}),
      /* **Kann der Kunde das selbst abholen, und was geht dann mit?**
         (Migration 0127) Der Posten traegt es von Anfang an mit, damit der
         Schalter in der Sammlung ohne zweite Abfrage dastehen kann. */
      ...(p?.abholung?.abholbar ? { abholbar: true } : {}),
      ...(p?.abholung?.anhaenger
        ? { anhaenger: alsAnhaenger(p.abholung.anhaenger) }
        : {}),
      bedient: p?.bedienungspflicht === "nur_mit_person",
    });
    if (gemerkt) ruf(warSchonGemerkt(gemerkt) ? "schonInSammlung" : "inSammlung", { was: name });
  };

  return (
    <div className="ns-blatt">
      {/* **Eine Zeile, zwei gleich schwere Hälften: Gerät links, Zeitraum
          rechts** (Luca, 17.09.2026). Kunde und Einsatzort fehlen hier mit
          Absicht — wer weiß, für wen es ist, vermietet und schlägt nicht
          nach. */}
      <div className="ns-zeile">
        <label className="ns-feld">
          <span>{t("vermieten.suchen")}</span>
          <input
            className="feld"
            type="search"
            autoFocus
            placeholder={t("vermieten.suchePlatz")}
            value={wort}
            onChange={(e) => { setzeWort(e.target.value); setzeMarkiert(null); }}
          />
        </label>
        <div className="ns-feld">
          <Zeitfrage
            id="ns-zeit"
            wert={wunsch}
            aendern={setzeWunsch}
            miettage={kopf.miettage}
            schritte={kopf.schritte}
            gemischt={kopf.gemischt}
            ende={kopf.bis}
          />
        </div>
      </div>

      {/* Der Kranz aus `stile/pillen.css` — dieselbe Pille wie im Brett,
          in den Ressourcen und in der Vermietfläche. **Ohne „Alle"**: Der
          Kranz IST die Frage und nicht der Filter auf eine Antwort, die
          schon dasteht. */}
      {(antwort.data?.gruppen ?? []).length ? (
        <div className="pillenkranz ns-gruppen" role="group" aria-label={t("nachschlagen.gruppen")}>
          {(antwort.data?.gruppen ?? []).map((g) => (
            <button
              key={g.id}
              type="button"
              className="pille"
              aria-pressed={gruppe === g.id}
              onClick={() => {
                setzeGruppe(gruppe === g.id ? null : g.id);
                setzeMarkiert(null);
              }}
            >
              {g.name} <span className="zahl">{g.zahl}</span>
            </button>
          ))}
        </div>
      ) : null}

      {antwort.isError ? (
        <FehlerBand pfad="fehler.unbekannt" nochmal={() => void antwort.refetch()} />
      ) : !gefragt ? (
        <p className="ns-leer">{t("nachschlagen.anfangBlatt")}</p>
      ) : antwort.isPending || (zeilen.length === 0 && !ladestandVon(antwort).hatDaten) ? (
          /* Ohne Antwort auf DIESE Frage kein „Nichts gefunden" (N12-01) —
             auch nicht, solange die Treffer des vorigen Wortes gehalten werden. */
        <Laedt zeilen={3} />
      ) : zeilen.length === 0 ? (
        <p className="ns-leer">
          {wort.trim()
            ? t("nachschlagen.nichts", { wort: wort.trim() })
            : t("nachschlagen.nichtsGruppe")}
        </p>
      ) : (
        <div className="tab-rahmen ns-rahmen">
          <div className="tab-fenster" ref={listeRef}>
            <div
              className={`tz-liste${tagBreite > 0 ? " mit-streifen" : ""}${antwort.isPlaceholderData ? " wird-frisch" : ""}`}
              style={rasterStil}
            >
              <Tagekopf tage={spalten} />
              {zeilen.map((z) => {
                const p = preisJe.get(z.typId);
                return (
                  <Trefferzeile
                    key={z.typId}
                    name={z.typ}
                    wo={[z.uebergruppe, z.gruppe].filter(Boolean).join(` ${t("allgemein.trennpunkt")} `)}
                    lage={z}
                    preis={p}
                    tage={spalten}
                    gewaehlt={markiert === z.typId}
                    waehlen={() => setzeMarkiert(markiert === z.typId ? null : z.typId)}
                    rechts={(
                      /* **„Merken" steht an der Zeile, nicht im Fuß.** Im
                         Verfügbarkeitscheck stand es unten an einer Stelle,
                         weil dort acht Zeilen acht gelbe Knöpfe bedeutet
                         hätten. Seit die Zeile dieselbe ist wie in der
                         Fläche, ist die Frage anders gestellt: Zwei
                         Bedienungen für denselben Griff sind teurer als ein
                         Knopf mehr im Bild — wer beide Wege benutzt, lernt
                         sonst zweimal dasselbe. */
                      <button
                        type="button"
                        className="vm-merken"
                        disabled={!bis}
                        onClick={() => { void merkenZeile(z); }}
                      >
                        {t("merkliste.merken")}
                      </button>
                    )}
                    kinder={(
                      <Trefferauskunft lage={z} preis={p} typ={stammJe.get(z.typId)} />
                    )}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Zwei Fußnoten, keine Tapete ──────────────────────────────

          Ein Satz, der bei jedem Aufruf gleich dasteht, gehört sonst nicht auf
          den Schirm (28-designkonzept.md). Diese beiden sind keine Erklärung,
          sondern die **Einheit der Zahlen darüber** — wie „netto" unter einer
          Summe: Die Lage folgt der Planung, und die Beträge sind Listenpreise.

          **Hier und nicht an der Zeile**, weil dieses Blatt gar nicht nach dem
          Kunden fragt: Es ist der Blick aus einer fremden Maske heraus. Am
          Telefon ist es umgekehrt — dort ist der Kunde wählbar, also ist es ein
          Zustand und steht am Preis (`NachschlagenTelefon.tsx`). */}
      <p className="ns-fuss">
        {t("nachschlagen.fuss")}
        {" "}
        {t("nachschlagen.fussListenpreis")}
      </p>
    </div>
  );
}
